"use client";

import { Badge } from "../badge";
import { Knopf } from "../knopf";
import { Zustandspille } from "../zustandspille";
import { feldIds, useFeldId } from "./feld";

/**
 * Wahl-Karten (Brötchen-Vorbestellung B2, Spec 2026-10-20, Entscheidungsvorlage Bedarf 1, Kandidat 1A; Design-Guide `wahl-karte.md`): Auswahl als
 * Raster von Karten mit Titel, Preis rechts und einer Zusatzzeile (z. B. Allergene). Zwei Arten:
 *  - `einzeln` (Radio): genau eine Option (Sorte). Die Eingabe ist `sr-only`, der Fokus liegt als Rahmen an der Karte (K5, nicht `hidden`).
 *  - `mehrfach` (Kontrollkästchen): mehrere Optionen (Beläge), das Kästchen ist sichtbar. Hat eine Option `mengeMax` > 1, steht in der
 *    gewählten Karte eine Mengenwahl − und + (bis `mengeMax`).
 * Gesperrte Optionen bleiben sichtbar, gedämpft und mit Satz an der Karte (r262: Grund am Element, keine stille Sperre). Ist eine Höchstzahl
 * erreicht, sperrt der Aufrufer die übrigen Optionen mit dem Satz im Kopf (`kopfRechts`), nie still.
 *
 * Der Baustein prüft keine Rechte und rechnet keine Preise: Werte, Sätze und Sperren liefert der Aufrufer (Anbieten = Erlauben, r110); die
 * Action prüft selbst. Feldfehler als Pille unter der Gruppe (AW-005).
 */
export interface WahlKarteOption {
  wert: string;
  titel: string;
  /** Preis bzw. Aufschlag als fertiger Text („0,60 €“, „+ 0,50 €“), rechts in der Karte. */
  preis?: string;
  /**
   * Marke unter dem Titel („Premium + 0,50 €“, Preismodell 20.10.2026, Mockup v3): additiv, nur wo ein Aufschlag wirkt. Steht statt eines Preises
   * rechts, wenn das Preismodell keine Preise je Karte zeigt.
   */
  badge?: string;
  /** Zusatzzeile (Allergene, Kennzeichnung). */
  zusatz?: string;
  /** Gesperrt mit Satz an der Karte („Heute nicht verfügbar“). */
  gesperrtSatz?: string;
  /** Nur `mehrfach`: höchstens so oft wählbar (Mengenwahl in der gewählten Karte). */
  mengeMax?: number;
}

/** Spaltenzahl ab `sm` bzw. `lg` (feste Klassenketten, nie zusammengesetzt). */
const RASTER: Record<2 | 4, string> = {
  2: "grid grid-cols-1 sm:grid-cols-2 gap-3",
  4: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3",
};

const KARTE =
  "relative h-full py-3 px-4 flex gap-x-3 text-sm bg-layer text-layer-foreground rounded-xl cursor-pointer ring-1 ring-layer-line has-checked:ring-2 has-checked:ring-primary-checked has-focus-visible:ring-2 has-focus-visible:ring-primary-focus has-disabled:opacity-50 has-disabled:cursor-default";

const KAESTCHEN =
  "shrink-0 mt-0.5 size-4 bg-transparent border-layer-line text-primary-checked rounded-full checked:bg-primary-checked checked:border-primary-checked focus:ring-0 focus:ring-offset-0 disabled:opacity-50";

const MINUS = (
  <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" />
  </svg>
);
const PLUS = (
  <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

export function WahlKarten({
  id: idVorgabe,
  legende,
  kopfRechts,
  name,
  art,
  optionen,
  gewaehlt,
  mengen,
  beiAenderung,
  beiMenge,
  spalten = 2,
  hilfe,
  fehler,
}: {
  id?: string;
  /** Sichtbare Legende („1 Sorte“, „2 Belag“). */
  legende: string;
  /** Satz rechts im Kopf („Höchstens 3 Beläge je Brötchen, 2 gewählt“). */
  kopfRechts?: string;
  name: string;
  art: "einzeln" | "mehrfach";
  optionen: readonly WahlKarteOption[];
  /** Gewählte Werte (kontrolliert); bei `einzeln` höchstens einer. */
  gewaehlt: readonly string[];
  /** Nur `mehrfach`: Menge je gewähltem Wert (fehlt = 1). */
  mengen?: Readonly<Record<string, number>>;
  beiAenderung: (neu: string[]) => void;
  /** Nur `mehrfach`: neue Menge einer gewählten Option. */
  beiMenge?: (wert: string, menge: number) => void;
  spalten?: 2 | 4;
  hilfe?: string;
  fehler?: string;
}) {
  const id = useFeldId(idVorgabe);
  const ids = feldIds(id);
  const beschreibung = [hilfe ? ids.hilfe : null, fehler ? ids.fehler : null].filter(Boolean).join(" ") || undefined;
  const umschalten = (wert: string, an: boolean) => {
    if (art === "einzeln") return beiAenderung(an ? [wert] : []);
    const neu = an ? [...gewaehlt, wert] : gewaehlt.filter((w) => w !== wert);
    beiAenderung(optionen.map((o) => o.wert).filter((w) => neu.includes(w)));
  };
  return (
    <>
      {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-selectable-cards-and-lists/grid-checkbox-cards (Raster aus Karten mit Titel, Zusatzzeile und Preis rechts; Radio-Art mit `sr-only` und Fokusrahmen an der Karte, K5; gesperrte Option mit Satz) */}
      <fieldset aria-describedby={beschreibung} aria-invalid={fehler ? true : undefined} className="min-w-0">
        <legend className="mb-3 w-full flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <span className="text-base font-medium text-foreground">{legende}</span>
          {kopfRechts && <span className="text-sm text-muted-foreground-1">{kopfRechts}</span>}
        </legend>
        <div className={RASTER[spalten]}>
          {optionen.map((o) => {
            const optionId = `${id}-${o.wert}`;
            const an = gewaehlt.includes(o.wert);
            const gesperrt = o.gesperrtSatz !== undefined && !an;
            const menge = mengen?.[o.wert] ?? 1;
            const mitMenge = art === "mehrfach" && an && (o.mengeMax ?? 1) > 1 && beiMenge !== undefined;
            return (
              <div key={o.wert} className="min-w-0">
                <label htmlFor={optionId} className={KARTE}>
                  <input
                    id={optionId}
                    type={art === "einzeln" ? "radio" : "checkbox"}
                    name={name}
                    value={o.wert}
                    className={art === "einzeln" ? "sr-only" : KAESTCHEN}
                    checked={an}
                    disabled={gesperrt}
                    onChange={(e) => umschalten(o.wert, e.target.checked)}
                  />
                  <span className="grow min-w-0">
                    <span className="flex items-start justify-between gap-x-3">
                      <span className="block font-medium text-foreground">{o.titel}</span>
                      {o.preis && <span className="shrink-0 tabular-nums text-foreground">{o.preis}</span>}
                    </span>
                    {o.badge && (
                      <span className="mt-1 block">
                        <Badge ton="primaer">{o.badge}</Badge>
                      </span>
                    )}
                    {o.gesperrtSatz && !an ? (
                      <span className="block text-xs text-muted-foreground-1">{o.gesperrtSatz}</span>
                    ) : (
                      o.zusatz && <span className="block text-xs text-muted-foreground-1">{o.zusatz}</span>
                    )}
                  </span>
                </label>
                {mitMenge && (
                  <div className="mt-2 flex items-center gap-x-2 ps-4">
                    <Knopf nurSymbol groesse="sm" label={`${o.titel}: einmal weniger`} symbol={MINUS} onClick={() => beiMenge!(o.wert, menge - 1)} disabled={menge <= 1} />
                    <span className="min-w-8 text-center text-sm tabular-nums text-foreground" aria-live="polite">{`${menge}×`}</span>
                    <Knopf nurSymbol groesse="sm" label={`${o.titel}: einmal mehr`} symbol={PLUS} onClick={() => beiMenge!(o.wert, menge + 1)} disabled={menge >= (o.mengeMax ?? 1)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {hilfe && (
          <p id={ids.hilfe} className="mt-2 text-sm text-muted-foreground-1">
            {hilfe}
          </p>
        )}
        {fehler && (
          <p id={ids.fehler} className="mt-2">
            {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
            <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
          </p>
        )}
      </fieldset>
    </>
  );
}
